import { useEffect, useRef, useState } from "react";
import { MLink } from "@/lib/marketing/m-link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { getLoginHostUrl } from "@/lib/marketing/login-host";
import { icon } from "@/lib/icons/icon";
import { XIcon as g_XIcon } from "@hugeicons/core-free-icons";
import { EMPLOYEES } from "@alm/domain/marketing";
import { NavLink } from "@/components/marketing/site/marketing-shell-types";
import { BrandMark } from "@/components/marketing/site/marketing-shell-brand-mark";
import { HangingMenu } from "@/components/marketing/site/marketing-shell-hanging-menu";
import { Hamburger } from "@/components/marketing/site/marketing-shell-hamburger";
import { MobileGroup } from "@/components/marketing/site/marketing-shell-mobile-group";
import { useShownElsewhere } from "@/components/marketing/site/use-shown-elsewhere";
import { cn } from "@/lib/utils";

const X = icon(g_XIcon);

const HOME_LINK: NavLink = { label: "Home", href: "/", testId: "link-nav-home" };

const OS_LINK: NavLink = {
  label: "Operating System",
  href: "/operating-system",
  testId: "link-features",
};

/* "Workforce" first, then every employee by name, read from the roster so a new hire appears on
 * joining. Order: Aria, Sarah and Nancy (published results), Olivia (hires), then the roster's
 * order. */
const MENU_FIRST = ["aria", "sarah", "nancy", "olivia"] as const;

const MENU_EMPLOYEES = [
  ...MENU_FIRST.map((slug) => EMPLOYEES.find((e) => e.slug === slug)).filter(
    (e) => e !== undefined,
  ),
  ...EMPLOYEES.filter((e) => !(MENU_FIRST as readonly string[]).includes(e.slug)),
];

const WORKFORCE_LINKS: NavLink[] = [
  { label: "Workforce", href: "/workforce", testId: "link-workforce" },
  ...MENU_EMPLOYEES.map((e) => ({
    label: e.name,
    href: `/workforce/${e.slug}`,
    testId: `link-workforce-nav-${e.slug}`,
  })),
];

/* What hangs under "Resources": the pages that support a decision rather than
 * make the argument. Case studies are linked here now that their text is
 * approved — the proof charts on /workforce already send readers to them. */
const RESOURCE_LINKS: NavLink[] = [
  { label: "Solutions", href: "/solutions", testId: "link-solutions" },
  { label: "Pricing", href: "/pricing", testId: "link-pricing" },
  { label: "Case Studies", href: "/case-studies", testId: "link-case-studies" },
  { label: "About", href: "/about", testId: "link-about" },
  { label: "Contact us", href: "/contact", testId: "link-contact" },
];

/** The source's threshold: the header is glass until the reader has moved. */
const HEADER_SOLID_AFTER_PX = 40;

/** Whether the header takes a ground of its own: not at rest over the opening, whose picture a
 * filled bar would cut, but from the first scroll, when copy passes under it. False on the server
 * and on first paint for the same reason. */
function useHeaderSolid(): boolean {
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    // state only on the crossing: eased scrolling runs this every frame
    let last: boolean | null = null;
    const measure = () => {
      const next = window.scrollY > HEADER_SOLID_AFTER_PX;
      if (next !== last) setSolid((last = next));
    };
    measure();
    window.addEventListener("scroll", measure, { passive: true });
    return () => window.removeEventListener("scroll", measure);
  }, []);
  return solid;
}

export function MarketingNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const solid = useHeaderSolid();
  const bar = useRef<HTMLElement>(null);
  /* The hero's own "Try it now" or "Book a demo" on screen: the bar's copy steps aside. */
  const tryShown = useShownElsewhere("/experience", bar);
  const demoShown = useShownElsewhere("/demo", bar);
  const away = "invisible opacity-0";

  return (
    <header
      ref={bar}
      data-solid={solid}
      className="sticky top-0 z-50 transition-[background-color] duration-300 data-[solid=true]:bg-background/92"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
        <BrandMark />

        {/* Desktop nav, from lg: at 768px its seven items and the mark ran past the screen. */}
        <nav className="hidden items-center gap-1 lg:flex">
          {[HOME_LINK].map((l) => (
            <Button
              key={l.href}
              variant="ghost"
              size="sm"
              className="font-ui"
              data-testid={l.testId}
              asChild
            >
              <MLink href={l.href}>{l.label}</MLink>
            </Button>
          ))}
          <HangingMenu label="AI Workforce" links={WORKFORCE_LINKS} testId="workforce" />
          <Button
            variant="ghost"
            size="sm"
            className="font-ui"
            data-testid={OS_LINK.testId}
            asChild
          >
            <MLink href={OS_LINK.href}>{OS_LINK.label}</MLink>
          </Button>
          <HangingMenu label="Resources" links={RESOURCE_LINKS} testId="resources" />
          <Button
            variant="outline"
            size="sm"
            className={cn("ml-1 font-ui transition-opacity", tryShown && away)}
            data-testid="link-experience"
            asChild
          >
            <MLink href="/experience">
              {/* The label alone: /experience also shows the AI employees, where Aria asks for an
                  email and Sarah a microphone, so each demo states its own terms. */}
              Try it now
            </MLink>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={cn("font-ui transition-opacity", demoShown && away)}
            data-testid="link-demo"
            asChild
          >
            <MLink href="/demo">Book a demo</MLink>
          </Button>
          <Button size="sm" className="font-ui" data-testid="link-signup" asChild>
            <MLink href="/signup">Sign up</MLink>
          </Button>
          <Button variant="ghost" size="sm" className="font-ui" data-testid="link-login" asChild>
            <a href={getLoginHostUrl()}>Sign in</a>
          </Button>
        </nav>

        {/* Mobile nav */}
        <div className="flex items-center gap-2 lg:hidden">
          {/* Two buttons and a brand mark do not fit on a 360px phone without
              the row overflowing, so below sm the no-account path is the first
              thing in the menu instead, framed rather than listed. */}
          <Button
            variant="outline"
            size="sm"
            className={cn("font-ui transition-opacity", tryShown && away)}
            data-testid="link-experience-compact"
            asChild
          >
            <MLink href="/experience" className="hidden sm:block">
              Try it now
            </MLink>
          </Button>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              {/* A conventional hamburger at a thumb's size: the 20px glyph in
                  a 36px button read as an afterthought on a phone. */}
              <Button
                variant="ghost"
                size="icon"
                className="size-11 font-ui [&_svg]:size-7"
                aria-label="Open menu"
                data-testid="button-mobile-menu"
              >
                <Hamburger />
              </Button>
            </SheetTrigger>
            {/* The shared sheet keeps itself open on an outside tap, which is right for a form and
                wrong for a menu: here a tap anywhere off the panel closes it, and so does one clear
                cross at the top. The primitive's own small close is hidden, so there is one. */}
            <SheetContent
              side="right"
              className="h-dvh w-72 font-ui [&>button]:hidden"
              onPointerDownOutside={() => setMobileOpen(false)}
              onInteractOutside={() => setMobileOpen(false)}
            >
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-left">Menu</SheetTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="-mr-2 size-11 [&_svg]:size-6"
                    aria-label="Close menu"
                    onClick={() => setMobileOpen(false)}
                    data-testid="button-mobile-menu-close"
                  >
                    <X strokeWidth={1.75} />
                  </Button>
                </div>
              </SheetHeader>
              {/* The list scrolls inside the panel: with Resources open it is taller
                  than a phone, and Sign in sat below the fold out of reach. */}
              <nav className="mt-6 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain pb-[max(1rem,env(safe-area-inset-bottom))]">
                {/* First, so a customer reaches the app in one tap from any page. */}
                <Button className="mb-2 w-full font-ui" data-testid="link-login-mobile" asChild>
                  <a href={getLoginHostUrl()} onClick={() => setMobileOpen(false)}>
                    Sign in
                  </a>
                </Button>
                <Button
                  variant="outline"
                  className="mb-2 w-full font-ui"
                  onClick={() => setMobileOpen(false)}
                  data-testid="link-signup-mobile"
                  asChild
                >
                  <MLink href="/signup">Sign up free</MLink>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start font-ui"
                  onClick={() => setMobileOpen(false)}
                  data-testid="link-experience-mobile"
                  asChild
                >
                  <MLink href="/experience">Try it now</MLink>
                </Button>
                <p className="mb-3 px-3 pt-2 text-xs text-muted-foreground">
                  Ask Aria, call Sarah, or run a med pass on sample data. No account.
                </p>
                <Button
                  variant="ghost"
                  className="w-full justify-start font-ui"
                  onClick={() => setMobileOpen(false)}
                  data-testid={`${HOME_LINK.testId}-mobile`}
                  asChild
                >
                  <MLink href={HOME_LINK.href}>{HOME_LINK.label}</MLink>
                </Button>
                {/* The two hanging menus, as collapsible groups. Resources opens by default because
                    those are the pages a decision needs; the workforce closes by default because
                    nine names in a phone menu is a wall, and the roster page is one tap. */}
                <MobileGroup
                  label="AI Workforce"
                  links={WORKFORCE_LINKS}
                  testId="workforce"
                  defaultOpen={false}
                  onNavigate={() => setMobileOpen(false)}
                />
                <Button
                  variant="ghost"
                  className="w-full justify-start font-ui"
                  onClick={() => setMobileOpen(false)}
                  data-testid={`${OS_LINK.testId}-mobile`}
                  asChild
                >
                  <MLink href={OS_LINK.href}>{OS_LINK.label}</MLink>
                </Button>
                <MobileGroup
                  label="Resources"
                  links={RESOURCE_LINKS}
                  testId="resources"
                  defaultOpen
                  onNavigate={() => setMobileOpen(false)}
                />
                <Button
                  variant="ghost"
                  className="w-full justify-start font-ui"
                  data-testid="link-demo-mobile"
                  asChild
                >
                  <MLink href="/demo" onClick={() => setMobileOpen(false)}>
                    Book a demo
                  </MLink>
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
